const getStorage = (storageName) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[storageName] || null;
  } catch (error) {
    return null;
  }
};

const AUTH_RECOVERABLE_CACHE_PREFIXES = [
  'ind-trades-',
  'ind-meta-',
  'ind-data-',
  'backtest-chart-',
];

const AUTH_STORAGE_PROBE_KEY = '__botspot_auth_storage_probe__';
const DEFAULT_AUTH_STORAGE_RECOVERY_CHARS = 512 * 1024;

export const isQuotaExceededError = (error) => {
  if (!error) {
    return false;
  }

  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014 ||
    String(error.message || '').toLowerCase().includes('quota')
  );
};

const isAuthRecoverableCacheKey = (key) => (
  AUTH_RECOVERABLE_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix))
);

export const pruneRecoverableAuthStorage = (
  storageName = 'localStorage',
  { minCharsToFree = DEFAULT_AUTH_STORAGE_RECOVERY_CHARS } = {}
) => {
  const storage = getStorage(storageName);
  if (!storage) {
    return { removedCount: 0, freedChars: 0 };
  }

  const entries = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !isAuthRecoverableCacheKey(key)) {
        continue;
      }
      const value = storage.getItem(key) || '';
      entries.push({
        key,
        chars: key.length + value.length,
      });
    }
  } catch {
    return { removedCount: 0, freedChars: 0 };
  }

  entries.sort((left, right) => right.chars - left.chars);

  let removedCount = 0;
  let freedChars = 0;
  for (const entry of entries) {
    try {
      storage.removeItem(entry.key);
      removedCount += 1;
      freedChars += entry.chars;
      if (freedChars >= minCharsToFree) {
        break;
      }
    } catch {
      // Continue pruning other cache entries if one removal fails.
    }
  }

  return { removedCount, freedChars };
};

export const safeGetStorageItem = (storageName, key) => {
  try {
    return getStorage(storageName)?.getItem(key) ?? null;
  } catch (error) {
    return null;
  }
};

export const safeSetStorageItem = (storageName, key, value) => {
  try {
    getStorage(storageName)?.setItem(key, value);
    return true;
  } catch (error) {
    // Storage may be unavailable in private/corrupt browser profiles.
    return false;
  }
};

export const safeRemoveStorageItem = (storageName, key) => {
  try {
    getStorage(storageName)?.removeItem(key);
  } catch (error) {
    // Storage may be unavailable in private/corrupt browser profiles.
  }
};

export const isStorageAvailable = (storageName) => {
  try {
    const storage = getStorage(storageName);
    if (!storage) {
      return false;
    }

    const probeKey = '__botspot_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return true;
  } catch (error) {
    return false;
  }
};

export const ensureAuthStorageCapacity = (storageName = 'localStorage') => {
  const storage = getStorage(storageName);
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(AUTH_STORAGE_PROBE_KEY, '1');
    storage.removeItem(AUTH_STORAGE_PROBE_KEY);
    return true;
  } catch (error) {
    try {
      storage.removeItem(AUTH_STORAGE_PROBE_KEY);
    } catch {
      // Ignore probe cleanup failure.
    }

    if (!isQuotaExceededError(error)) {
      return false;
    }
  }

  const recovery = pruneRecoverableAuthStorage(storageName);
  if (recovery.removedCount > 0) {
    console.warn('[browserStorage] Recovered auth storage capacity by pruning derived cache entries:', {
      removedCount: recovery.removedCount,
      freedChars: recovery.freedChars,
    });
  }

  try {
    storage.setItem(AUTH_STORAGE_PROBE_KEY, '1');
    storage.removeItem(AUTH_STORAGE_PROBE_KEY);
    return true;
  } catch {
    try {
      storage.removeItem(AUTH_STORAGE_PROBE_KEY);
    } catch {
      // Ignore probe cleanup failure.
    }
    return false;
  }
};

export const setCriticalStorageItem = (storageName, key, value) => {
  const storage = getStorage(storageName);
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      return false;
    }
  }

  const recovery = pruneRecoverableAuthStorage(storageName, {
    minCharsToFree: Math.max(DEFAULT_AUTH_STORAGE_RECOVERY_CHARS, String(value).length * 2),
  });
  if (recovery.removedCount > 0) {
    console.warn('[browserStorage] Recovered critical storage write by pruning derived cache entries:', {
      key,
      removedCount: recovery.removedCount,
      freedChars: recovery.freedChars,
    });
  }

  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};
